import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMonitoringAiSettings } from './useMonitoringAiSettings';
import {
  fetchCentralAiStatus,
  pingCentralAiPresence,
  leaveCentralAiPresence,
  sendLeaveCentralAiPresenceKeepalive,
  centralAiHasActiveMonitoringTargets,
} from '../services/centralAiMonitorApi';
import { isShortPoolCctvPopupPath } from '../components/monitoring/cctvPopupRoutes';
import { useAuth } from '../contexts/AuthContext';

const PRESENCE_INTERVAL_MS = 15_000;

/**
 * 통합 관제·시설 CCTV 전체화면(inplace)이 열려 있는 동안 서버 AI 루프 하트비트.
 * presence가 없으면 central-ai 워커가 스냅샷을 비우고 bbox가 전부 사라진다.
 */
export function useCentralAiOpsPresence(opsSessionActive: boolean) {
  const { authState } = useAuth();
  const location = useLocation();
  const onPoolCctvPopup = isShortPoolCctvPopupPath(location.pathname);
  const opsReady =
    authState.isAuthenticated &&
    !authState.loading &&
    (authState.isAdminPasswordVerified || onPoolCctvPopup);
  const { settings } = useMonitoringAiSettings(opsReady && opsSessionActive);
  const localIncludedKey = settings.included_pool_ids.join(',');

  useEffect(() => {
    if (!opsSessionActive || !opsReady || !settings.server_ai_enabled) {
      return undefined;
    }

    const send = async () => {
      try {
        const optIn = settings.central_monitor_opt_in !== false;
        const localTargets =
          optIn && settings.included_pool_ids.length > 0;
        // 시설 전체화면: included에 없어도 이 화면을 보고 있으면 추론 유지
        if (localTargets || onPoolCctvPopup) {
          await pingCentralAiPresence();
          return;
        }
        const st = await fetchCentralAiStatus();
        if (!st.server_ai_enabled) return;
        if (!centralAiHasActiveMonitoringTargets(st)) return;
        await pingCentralAiPresence();
      } catch (e) {
        console.warn('관제 세션 presence 전송 실패:', e);
      }
    };

    const leaveOnUnload = () => {
      sendLeaveCentralAiPresenceKeepalive();
    };

    const leaveOnUnmount = () => {
      sendLeaveCentralAiPresenceKeepalive();
      void leaveCentralAiPresence().catch(() => undefined);
    };

    void send();
    const id = window.setInterval(() => void send(), PRESENCE_INTERVAL_MS);
    window.addEventListener('pagehide', leaveOnUnload);
    window.addEventListener('beforeunload', leaveOnUnload);

    return () => {
      window.clearInterval(id);
      window.removeEventListener('pagehide', leaveOnUnload);
      window.removeEventListener('beforeunload', leaveOnUnload);
      leaveOnUnmount();
    };
  }, [
    opsSessionActive,
    opsReady,
    onPoolCctvPopup,
    settings.server_ai_enabled,
    settings.central_monitor_opt_in,
    localIncludedKey,
  ]);
}
